/* To døgn med strøm som én stripe, og tidevannet som forklarer den.
 *
 * HVORFOR EN EGEN TIDSAKSE OG IKKE SYKLUSEN UNDER KARTET
 *   Syklusen er uker gjennom en generasjon. Dette er timer gjennom to døgn. Det
 *   er to forskjellige spørsmål på to forskjellige akser, og å legge dem på den
 *   samme hadde gjort den ene ubrukelig for å slippe en stripe til.
 *
 * HVORFOR TIDEVANNET LIGGER I DEN SAMME FIGUREN
 *   Strømmen snur fordi tidevannet snur. Uten flo og fjære er stripa en rekke
 *   piler som skifter retning uten grunn; med dem er den en fjord som puster.
 *   Forklaringen ved siden av det den forklarer er forskjellen på et varsel
 *   noen stoler på og et noen bare leser.
 *
 * HVORFOR STILLE VANN ER EN PRIKK OG IKKE EN LAV SØYLE
 *   En søyle på to piksler og ingen søyle ser like ut, og de betyr helt
 *   forskjellige ting: «vi spurte, og vannet står nesten stille» mot «vi fikk
 *   ikke svar». Prikken er den tredje figuren, og den finnes fordi de to andre
 *   allerede var opptatt.
 *
 * SKALAEN ER FAST
 *   Søylehøyden måles mot 0,15 m/s og ikke mot den sterkeste timen i vinduet.
 *   Normalisert ville det roligste døgnet i året sett ut som det verste, med
 *   aksen stilltiende skalert under. Leseren drar i denne stripa nettopp for å
 *   se forskjellen mellom timer.
 */

import { useEffect, useRef, useState } from 'react'

import type { SiteFlow } from '../api'
import { useT } from '../lang'
import { clock } from '../time'

const H = 46
const TOPP = 6          // luft over den høyeste søyla
const BUNN = 13         // plass til klokkeslettene
const FULL_MS = 0.15    // søylehøyde ved denne farten; se filhodet

/** Kompassnavnet for en retning, som en fisker sier den.
 *
 *  Grader står i teksten ved siden av. Et himmelstrøk leses på et blunk og en
 *  gradverdi kan sammenlignes med neste time; her trengs begge, så begge er
 *  der og ingen av dem må gjettes fra den andre. */
const ROSE = ['N', 'NØ', 'Ø', 'SØ', 'S', 'SV', 'V', 'NV']

export function kompass(deg: number): string {
  return ROSE[Math.round(((deg % 360) + 360) % 360 / 45) % 8]!
}

export function Stroemstripe({ flow, valgt, onVelg }: {
  flow: SiteFlow
  /** Indeks i `flow.hours`. */
  valgt: number
  onVelg: (i: number) => void
}) {
  const t = useT()
  const el = useRef<SVGSVGElement>(null)
  const [drar, setDrar] = useState(false)

  const timer = flow.hours
  const n = timer.length
  const W = Math.max(1, n) * 10
  const kol = (i: number) => i * 10 + 5

  /* Nå-linja. Den nærmeste timen framover, ikke den nærmeste i begge
     retninger: en time som allerede er gått er ikke et varsel. */
  const naa = Date.now()
  const funnet = timer.findIndex(
    (h) => new Date(h.time).getTime() >= naa - 30 * 60_000)
  /* -1 betyr at hele vinduet ligger bak oss -- et varsel ingen har hentet på
     to døgn. Da står NÅ i enden og ikke i starten, som ville påstått at den
     eldste timen er den vi er i. */
  const naaIdx = funnet < 0 ? n - 1 : funnet

  const hoyde = (h: (typeof timer)[number]) => {
    if (!h.current) return null
    const f = Math.min(1, h.current.speed_ms / FULL_MS)
    return (H - TOPP - BUNN) * f
  }

  /* Tidevannet plassert på den samme aksen som timene, slik at flo står der
     flo faktisk er og ikke der en egen akse ville satt den. */
  const t0 = timer[0] ? new Date(timer[0].time).getTime() : 0
  const tidevann = (flow.tide_extremes ?? []).map((e) => {
    const x = (new Date(e.time).getTime() - t0) / 3_600_000
    return { ...e, x: kol(x) }
  }).filter((e) => e.x >= 0 && e.x <= W)

  const velgFra = (clientX: number) => {
    const r = el.current?.getBoundingClientRect()
    if (!r || r.width === 0) return
    const i = Math.floor(((clientX - r.left) / r.width) * n)
    onVelg(Math.max(0, Math.min(n - 1, i)))
  }

  /* Ingen avhengighetsliste, med vilje. Lytterne byttes ut ved hver render,
     slik at `velgFra` alltid holder gjeldende `n` og `onVelg` -- under en
     dragning er det nettopp disse som kan ha endret seg, og en foreldet
     lukning ville skrevet feil time. Det koster to addEventListener-kall per
     render, og bare mens noen drar. */
  useEffect(() => {
    if (!drar) return
    const move = (e: PointerEvent) => velgFra(e.clientX)
    const up = () => setDrar(false)
    window.addEventListener('pointermove', move)
    window.addEventListener('pointerup', up)
    return () => {
      window.removeEventListener('pointermove', move)
      window.removeEventListener('pointerup', up)
    }
  })

  /* Etter krokene, aldri før: en tidlig retur over en useEffect endrer
     rekkefølgen på krokene mellom to rendringer. */
  if (n === 0) return null

  const her = timer[valgt]

  return (
    <div className="stroemstripe">
      <div className="stroemstripe-tall">
        {her?.current ? (
          <>
            <b>{kompass(her.current.bearing_toward)}</b>
            <span className="stroemstripe-fart">
              {her.current.slack
                ? t('slack water')
                /* Tilde: modellert, ikke målt. Samme merking som resten av
                   produktet bruker for et tall ingen har lest av. */
                : `~${her.current.speed_ms.toFixed(3)} m/s`}
            </span>
            <span className="stroemstripe-grad">
              {Math.round(her.current.bearing_toward)}°
            </span>
            {/* THE VERDICT, IN WORDS. The arrows carry it as colour, which is
                quick but not nameable; a farmer asked to act on this has to be
                able to say what it said. About EXCHANGE and not about oxygen:
                whether it is enough depends on the fish in the ring. */}
            {her.current.exchange && (
              <span className={`stroemstripe-band er-${her.current.exchange}`}>
                {t(`exchange:${her.current.exchange}`)}
              </span>
            )}
          </>
        ) : (
          <span className="stroemstripe-fart">{t('no forecast this hour')}</span>
        )}
        <span className="stroemstripe-tid">
          {her ? clock(new Date(her.time).getTime() / 1000) : ''}
        </span>
      </div>

      <svg ref={el} className="stroemstripe-flate"
           viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="none"
           role="slider" tabIndex={0}
           aria-label={t('Hour of the current forecast')}
           aria-valuemin={0} aria-valuemax={n - 1} aria-valuenow={valgt}
           onPointerDown={(e) => { setDrar(true); velgFra(e.clientX) }}
           onKeyDown={(e) => {
             if (e.key === 'ArrowLeft') onVelg(Math.max(0, valgt - 1))
             if (e.key === 'ArrowRight') onVelg(Math.min(n - 1, valgt + 1))
           }}>
        {/* Grunnlinja, som søylene står på. */}
        <line x1={0} x2={W} y1={H - BUNN} y2={H - BUNN}
              className="stroemstripe-gulv" />

        {tidevann.map((e, i) => (
          <g key={i}>
            <line x1={e.x} x2={e.x} y1={TOPP - 4} y2={H - BUNN}
                  className="stroemstripe-tidevann" />
            <text x={e.x} y={TOPP - 6} textAnchor="middle"
                  className="stroemstripe-flomerke">
              {e.type === 'high' ? t('tide:high') : t('tide:low')}
            </text>
          </g>
        ))}

        {timer.map((h, i) => {
          const hh = hoyde(h)
          const x = kol(i)
          if (hh === null) return null
          if (h.current?.slack) {
            return <circle key={i} cx={x} cy={H - BUNN - 3} r={1.8}
                           className="stroemstripe-stille" />
          }
          return (
            <rect key={i} x={x - 3} y={H - BUNN - hh} width={6} height={hh}
                  className={'stroemstripe-soyle'
                             + (i === valgt ? ' er-valgt' : '')} />
          )
        })}

        {/* NÅ, og deretter er alt til høyre et varsel. */}
        <line x1={kol(naaIdx)} x2={kol(naaIdx)} y1={TOPP - 4} y2={H - BUNN + 3}
              className="stroemstripe-naa" />

        {timer.map((h, i) => (
          i % 6 === 0 ? (
            <text key={i} x={kol(i)} y={H - 3} textAnchor="middle"
                  className="stroemstripe-klokke">
              {clock(new Date(h.time).getTime() / 1000)}
            </text>
          ) : null
        ))}
      </svg>
    </div>
  )
}
